import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Input, Select, Tag, Space, Typography, Button, Modal, Alert, Row, Col, message } from 'antd';
import { LinkOutlined, DisconnectOutlined, HistoryOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { doctorService, patientService } from '../../services';

const { Text, Paragraph } = Typography;

// Visits imported from the doctor's previous system. They came across with no
// patient attached - the old export had no table saying which visit belonged to
// whom - so this page is where the doctor reads a record, recognises it, and says
// whose it was.
const LegacyRecords = () => {
    const { t } = useTranslation();
    const [records, setRecords] = useState([]);
    const [total, setTotal] = useState(0);
    const [unattached, setUnattached] = useState(0);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [attached, setAttached] = useState('no');
    const [page, setPage] = useState(1);

    const [attaching, setAttaching] = useState(null);
    const [patients, setPatients] = useState([]);
    const [patientSearch, setPatientSearch] = useState('');
    const [chosenPatient, setChosenPatient] = useState(null);

    const fetchRecords = useCallback(async () => {
        setLoading(true);
        try {
            const response = await doctorService.getLegacyRecords({ search, attached, page, pageSize: 20 });
            setRecords(response.data || []);
            setTotal(response.total || 0);
            setUnattached(response.unattached || 0);
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    }, [search, attached, page, t]);

    useEffect(() => {
        fetchRecords();
    }, [fetchRecords]);

    // The patient list backing the attach dialog. Loaded once; the dialog filters
    // it in the browser, which is instant and avoids a request per keystroke.
    useEffect(() => {
        patientService.getPatients()
            .then((response) => setPatients(response.data || []))
            .catch(() => setPatients([]));
    }, []);

    const openAttach = (record) => {
        setAttaching(record);
        setChosenPatient(record.patientId?._id || null);
        setPatientSearch('');
    };

    const saveAttachment = async (patientId) => {
        try {
            await doctorService.attachLegacyRecord(attaching._id, patientId);
            message.success(t('success'));
            setAttaching(null);
            fetchRecords();
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        }
    };

    // What this record actually holds, so a doctor can tell at a glance whether it
    // is worth opening.
    const contents = (record) => {
        const parts = [];
        if (record.prescriptionText) parts.push({ key: 'rx', color: 'purple', label: t('prescriptions') });
        if (record.investigation && Object.values(record.investigation).some(Boolean)) {
            parts.push({ key: 'lab', color: 'blue', label: t('labTests') });
        }
        if (record.ctScan && Object.values(record.ctScan).some(Boolean)) {
            parts.push({ key: 'ct', color: 'geekblue', label: 'CT' });
        }
        if (record.surgery && Object.values(record.surgery).some(Boolean)) {
            parts.push({ key: 'surg', color: 'red', label: t('surgery') });
        }
        if (record.examination && Object.values(record.examination).some(Boolean)) {
            parts.push({ key: 'exam', color: 'green', label: t('examination') });
        }
        return parts;
    };

    const preview = (record) => [
        record.surgery?.name,
        record.ctScan?.findings,
        record.prescriptionText,
        record.examination?.generals,
        record.investigation?.notes
    ].filter(Boolean).join(' · ');

    const columns = [
        {
            title: t('visitNumber'),
            dataIndex: 'legacyVisitId',
            key: 'legacyVisitId',
            width: 110,
            render: (value) => <Text strong>#{value}</Text>
        },
        {
            title: t('contains'),
            key: 'contains',
            width: 260,
            render: (_, record) => (
                <Space size={4} wrap>
                    {contents(record).map((part) => (
                        <Tag key={part.key} color={part.color}>{part.label}</Tag>
                    ))}
                </Space>
            )
        },
        {
            title: t('details'),
            key: 'preview',
            render: (_, record) => (
                <Text type="secondary" style={{ fontSize: 12 }} ellipsis={{ tooltip: preview(record) }}>
                    {preview(record).slice(0, 160)}
                </Text>
            )
        },
        {
            title: t('patient'),
            key: 'patient',
            width: 190,
            render: (_, record) => (record.patientId
                ? <Tag color="green">{record.patientId.name}</Tag>
                : <Text type="secondary">{t('notAttached')}</Text>)
        },
        {
            title: t('actions'),
            key: 'actions',
            width: 150,
            render: (_, record) => (
                <Button
                    size="small"
                    type={record.patientId ? 'default' : 'primary'}
                    icon={record.patientId ? <DisconnectOutlined /> : <LinkOutlined />}
                    onClick={() => openAttach(record)}
                >
                    {record.patientId ? t('change') : t('attachToPatient')}
                </Button>
            )
        }
    ];

    const expandedRowRender = (record) => {
        const rows = [
            [t('prescriptions'), record.prescriptionText],
            [t('surgery'), [record.surgery?.name, record.surgery?.notes].filter(Boolean).join(' — ')],
            ['CT', [record.ctScan?.findings, record.ctScan?.notes].filter(Boolean).join(' — ')],
            [t('examination'), Object.entries(record.examination || {})
                .filter(([, value]) => value).map(([key, value]) => `${key}: ${value}`).join('\n')],
            [t('labTests'), Object.entries(record.investigation || {})
                .filter(([, value]) => value).map(([key, value]) => `${key}: ${value}`).join('\n')]
        ].filter(([, value]) => value);

        return (
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {rows.map(([label, value]) => (
                    <div key={label}>
                        <Text strong>{label}</Text>
                        <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{value}</Paragraph>
                    </div>
                ))}
            </Space>
        );
    };

    const visiblePatients = patients
        .filter((patient) => {
            const term = patientSearch.trim().toLowerCase();
            if (!term) return true;
            return [patient.name, patient.phoneNumber].filter(Boolean)
                .some((value) => value.toLowerCase().includes(term));
        })
        .slice(0, 50);

    return (
        <div className="page-container">
            <Row gutter={[16, 16]} align="middle" className="page-header">
                <Col xs={24}>
                    <h1 className="page-title">
                        <Space><HistoryOutlined />{t('legacyRecords')}</Space>
                    </h1>
                </Col>
            </Row>

            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={`${unattached} ${t('recordsNotYetAttached')}`}
                description={t('legacyRecordsHint')}
            />

            <Card style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={16}>
                        <Input.Search
                            allowClear
                            placeholder={t('searchLegacyRecords')}
                            onSearch={(value) => { setSearch(value); setPage(1); }}
                            onChange={(e) => { if (!e.target.value) { setSearch(''); setPage(1); } }}
                        />
                    </Col>
                    <Col xs={24} md={8}>
                        <Select
                            style={{ width: '100%' }}
                            value={attached}
                            onChange={(value) => { setAttached(value); setPage(1); }}
                            options={[
                                { value: 'no', label: t('notAttached') },
                                { value: 'yes', label: t('attached') },
                                { value: '', label: t('all') }
                            ]}
                        />
                    </Col>
                </Row>
            </Card>

            <Card className="table-card">
                <Table
                    columns={columns}
                    dataSource={records}
                    rowKey="_id"
                    loading={loading}
                    scroll={{ x: 1000 }}
                    expandable={{ expandedRowRender }}
                    pagination={{
                        current: page,
                        total,
                        pageSize: 20,
                        showSizeChanger: false,
                        onChange: setPage,
                        showTotal: (count) => `${t('total')}: ${count}`
                    }}
                />
            </Card>

            <Modal
                open={Boolean(attaching)}
                title={`${t('attachToPatient')} — #${attaching?.legacyVisitId ?? ''}`}
                onCancel={() => setAttaching(null)}
                footer={[
                    ...(attaching?.patientId ? [(
                        <Button key="detach" danger onClick={() => saveAttachment(null)}>
                            {t('detach')}
                        </Button>
                    )] : []),
                    <Button key="cancel" onClick={() => setAttaching(null)}>{t('cancel')}</Button>,
                    <Button
                        key="save"
                        type="primary"
                        disabled={!chosenPatient}
                        onClick={() => saveAttachment(chosenPatient)}
                    >
                        {t('save')}
                    </Button>
                ]}
            >
                {attaching && (
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        <Paragraph type="secondary" style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                            {preview(attaching).slice(0, 400)}
                        </Paragraph>

                        <Input
                            placeholder={t('searchBy') + ' ' + t('name')}
                            value={patientSearch}
                            onChange={(e) => setPatientSearch(e.target.value)}
                        />

                        <Select
                            style={{ width: '100%' }}
                            placeholder={t('selectPatient')}
                            value={chosenPatient}
                            onChange={setChosenPatient}
                            showSearch={false}
                            options={visiblePatients.map((patient) => ({
                                value: patient._id,
                                label: `${patient.name}${patient.phoneNumber ? ` — ${patient.phoneNumber}` : ''}`
                            }))}
                            notFoundContent={t('noData')}
                        />
                    </Space>
                )}
            </Modal>
        </div>
    );
};

export default LegacyRecords;
